/**
 * Cookie-based session probe + sign-in/out URL helpers for the simpl_auth
 * migration (Tier 3a).
 *
 * Today: the SPA is gated by the `SIMPL_AUTH_JWT_<env>` cookie set by
 * simpl_auth on `.simpl.org`. The Amplify Authenticator UI has been
 * removed; `simplAuthSignInUrl()` redirects the browser directly to
 * `auth[-env].simpl.org/authorize`. simpl_auth runs the full sign-in /
 * MFA-enrollment / MFA-challenge flow and bounces the browser back
 * with a cookie set on the shared parent domain.
 *
 * Bridge for existing users: the SPA's `<SimplAuthGuard>` probes the
 * session at mount, and if the probe says `unauthed` it tries the
 * §6.7 cognito-exchange bridge (`cognitoExchangeBridge.ts`) before
 * falling through to the redirect. That bridge silently trades a live
 * Amplify/Cognito session for a `SIMPL_AUTH_JWT_<env>` cookie, so
 * users who happen to land on the SPA with a leftover Cognito session
 * are not bounced through simpl_auth's `/sign-in` form. Once the
 * `cognito-exchange` endpoint sunsets, the bridge can be deleted and
 * `unauthed` flows straight to the redirect.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { simplApiRequest, SimplApiHttpError } from "../../lib/simpl-api-client";
import { attemptCognitoExchange } from "./cognitoExchangeBridge";
import {
  CallerClaims,
  Role,
  rolesFromAuthUser
} from "./roles";

export type SimplAuthSessionStatus =
  | "idle"
  | "loading"
  | "bridging"
  | "authed"
  | "unauthed"
  /**
   * The whoami probe returned 404 — the JWT is valid and identifies a
   * caller, but simpl-api has no ``user`` row for that email. Signing
   * in again cannot fix this (the same JWT would be re-issued), so the
   * guard renders an "Account not provisioned" surface with a sign-out
   * escape hatch instead of redirecting.
   */
  | "account-not-provisioned"
  /**
   * The whoami probe returned 403 — the JWT is valid but the caller is
   * explicitly forbidden. Same shape as ``account-not-provisioned``: a
   * redirect is useless, but the user still needs a way to sign out.
   */
  | "forbidden"
  /**
   * The whoami probe hit a 5xx, network failure, timeout, or a 200
   * whose body was structurally invalid (no caller claims). None of
   * those are curable by re-signing-in, so the guard renders a
   * "Service unavailable" surface with retry + sign-out affordances.
   */
  | "service-unavailable";

export type SimplAuthSession = {
  status: SimplAuthSessionStatus;
  /**
   * Caller claims as resolved by the simpl-auth-authorizer Lambda. Only
   * populated when `status === "authed"`. The shape mirrors the `caller`
   * field on `/get-user` responses.
   */
  caller: CallerClaims | null;
  /** Roles derived from `caller` — convenience for gating UI in `<SimplAuthGuard>`. */
  roles: Role[];
  /**
   * HTTP status the whoami probe returned (or ``null`` if the request
   * never reached the server — network failure / timeout / CORS). Only
   * populated for the "signed in but broken" statuses
   * (``account-not-provisioned`` / ``forbidden`` /
   * ``service-unavailable``). Surfaced to the developer-diagnostic
   * paragraph on the error surfaces so triage doesn't need devtools.
   */
  probeStatus?: number | null;
  /**
   * Re-runs the whoami probe. Only set on ``service-unavailable`` — the
   * only status whose cause can plausibly resolve without user action
   * (transient 5xx, brief network hiccup). The other terminal statuses
   * need real intervention (provisioning, admin unblock) and expose no
   * retry so the surface can't loop the user in place.
   */
  retry?: () => void;
};

/**
 * Resolves the simpl_auth host for the current build environment. The
 * SPA never talks to this host directly for API calls (those go through
 * `simpl-api-client.ts`), but it does use it for sign-in/out redirects.
 *
 * Priority:
 *   1. `REACT_APP_AUTH_HOST` build-time override — useful for one-off
 *      review apps and CI smoke tests pointed at custom auth stacks.
 *      Note: `deploy-frontend.sh` always sets this inline so a
 *      developer's `.env.local` can't leak `localhost:8088` into a
 *      deployed bundle.
 *   2. Per-`REACT_APP_ENV` default, from an INCLUDE-LIST of the
 *      environments that have their own simpl_auth deploy. Three exist:
 *      `auth.simpl.org` (prod), `auth-staging.simpl.org` and
 *      `auth-stg.simpl.org`. Every other env trusts staging's, which is
 *      what `simpl_auth_issuer_url` in
 *      `simpl-api/terraform/accounts/plsc/<env>/main.tf` says for all of
 *      them. Mirrors `deploy-frontend.sh`, so the fallback and the value
 *      the deploy script injects cannot disagree.
 *
 *      The old comment here claimed only TWO stacks existed and that
 *      `auth-stg` would 404. That is now false — SE-1560's factory-env
 *      work gave `stg` its own — but the correction is NOT "derive
 *      `auth-${env}` for everything": `auth-dev`, `auth-sadev`,
 *      `auth-hsdev` and `auth-testone` are all NXDOMAIN, so a derivation
 *      points those envs at nothing.
 *
 *      Why an include-list rather than a derivation: the auth host, the
 *      cookie name and the issuer are REALM values (see the taxonomy at
 *      the top of `simpl_auth/tf/locals.tf`). Adding an env here is a
 *      realm change and all three must move together, or the env trusts
 *      one stack and reads another's cookie.
 *   3. `http://localhost:8088` for `local` / unset — the dev-proxy
 *      front-door port (the `proxy` service in
 *      `simpl-api/.devcontainer/docker-compose.yml`; nginx config in
 *      `simpl-api/dev-proxy/`). The proxy fans `/api/auth/*`,
 *      `/authorize`, `/sign-out`, etc. out to a real `simpl_auth`
 *      running on `:8888`, so the bridge cookie set there is visible
 *      to the SPA on the same origin.
 */
/**
 * The simpl_auth host the SPA talks to.
 *
 * SINGLE SOURCE OF TRUTH is `simpl-api/deploy-frontend.sh`'s per-env
 * `case` — it sets `REACT_APP_AUTH_HOST` at build time. This function
 * just returns that value (or a small set of defaults when it's not
 * set — production, local dev). See the header comment on
 * `authMode.ts` for why the parallel env-lists that used to live here
 * (`ENVS_WITH_OWN_AUTH_STACK`, `AUTH_STACK_PIGGYBACK`) were removed.
 */
export function getSimplAuthHost(): string {
  const explicit = process.env.REACT_APP_AUTH_HOST;
  if (explicit && explicit.trim()) {
    return explicit.replace(/\/+$/, "");
  }

  const env = process.env.REACT_APP_ENV?.trim().toLowerCase();
  if (!env || env === "local") {
    return "http://localhost:8088";
  }
  if (env === "production" || env === "prod") {
    return "https://auth.simpl.org";
  }
  // A deployed env without REACT_APP_AUTH_HOST is a build that skipped
  // deploy-frontend.sh's case — fall back to canonical staging's
  // simpl_auth, which is what the old code did for every unknown env.
  return "https://auth-staging.simpl.org";
}

function buildAuthUrl(path: string, returnTo?: string): string {
  const host = getSimplAuthHost();
  const base = `${host}${path.startsWith("/") ? path : `/${path}`}`;
  if (!returnTo) return base;
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}redirect_uri=${encodeURIComponent(returnTo)}`;
}

/**
 * URL that begins a simpl_auth sign-in flow. `returnTo` defaults to the
 * current `window.location.href` so the SPA reloads at the same page
 * after sign-in completes. Caller is responsible for validating
 * `returnTo` against any allow-list — simpl_auth itself enforces a
 * `*.simpl.org` (and dev-localhost) allow-list on the auth side.
 */
export function simplAuthSignInUrl(returnTo?: string): string {
  const target =
    returnTo ?? (typeof window !== "undefined" ? window.location.href : "/");
  return buildAuthUrl("/authorize", target);
}

/**
 * URL that ends a simpl_auth session. Clears the JWT cookie and the
 * AIOHTTP_SESSION cookie on the auth host, then redirects to
 * `returnTo`. The SPA's sign-out helper (`features/Auth/signOut.ts`)
 * also performs a best-effort `Auth.signOut()` to clear any residual
 * Amplify localStorage from the bridge era before navigating here.
 */
export function simplAuthSignOutUrl(returnTo?: string): string {
  const target =
    returnTo ?? (typeof window !== "undefined" ? window.location.origin : "/");
  return buildAuthUrl("/sign-out", target);
}

/**
 * Probes the API for a valid `simpl_auth` session using `/get-user`
 * with no params — the handler falls back to the caller's email from
 * the authorizer context, so this doubles as a `whoami` check without
 * needing a separate Lambda.
 *
 * Critically, the probe runs with `skipCognitoBearer: true` so the
 * Amplify/Cognito ID token is **not** attached as a fallback. The
 * dual-issuer authorizer accepts both Cognito and `simpl_auth` JWTs
 * during the §6.7 sunset window, so a probe that included the Bearer
 * fallback would silently auth via Cognito for any user with leftover
 * Amplify state — bypassing the cognito-exchange bridge entirely and
 * never minting a `simpl_auth` cookie or triggering MFA enrollment.
 * The probe is the one place the SPA needs to know "do I have a
 * `simpl_auth` cookie *specifically*?", so it must reject the Cognito
 * fallback. Every other simpl-api request keeps the dual-issuer
 * behavior so the app stays usable while the bridge runs and burns.
 *
 * Status classification — the crux of why this function exists as its
 * own layer instead of collapsing everything into "authed vs unauthed":
 *
 *   - 200 with `caller.email`  → ``authed``.
 *   - 200 without `caller.email` → ``service-unavailable``. The
 *     Lambda returned a structurally invalid whoami body (`caller`
 *     null on a route that only reaches this line when the authorizer
 *     accepted a JWT). Re-signing-in cannot patch a server-side
 *     serialization bug, so we do NOT redirect.
 *   - 401 → ``unauthed``. Only ``unauthed`` triggers the sign-in
 *     redirect (via `<SignInRedirect>`). This is the only status
 *     where a fresh sign-in materially changes the input to the next
 *     probe.
 *   - 403 → ``forbidden``. JWT valid, caller rejected. A fresh JWT
 *     for the same identity gets rejected identically.
 *   - 404 → ``account-not-provisioned``. JWT valid, no `user` row.
 *     A fresh JWT still can't conjure a DB row.
 *   - 5xx / network / timeout / parse failure → ``service-unavailable``.
 *     The API side has broken in a way sign-in can't touch.
 *
 * The pre-SE-1765 version coerced every non-401 case to ``unauthed``,
 * which produced the "signed in, whoami fails, redirect to sign in,
 * mint the same JWT, whoami fails again" tight loop — the browser
 * hammering `/authorize` several times a second with no visible
 * escape hatch. Splitting the statuses is what breaks that loop:
 * every non-401 terminal state now renders a surface with a Sign out
 * button, so a real user hitting an API outage or a provisioning gap
 * has a way out without shell access.
 *
 * Designed to be called once on bootstrap; also invoked by
 * ``useSimplAuthSession``'s ``retry`` for the ``service-unavailable``
 * surface. Idempotent — no client-side state mutated.
 */
export async function probeSimplAuthSession(
  skipCognitoBearer = true
): Promise<SimplAuthSession> {
  try {
    // `/api/user` with no params is the get-user "whoami" fallback —
    // the handler resolves the caller's email from the authorizer
    // context and returns their User row. See
    // `api/get-user/src/index.ts` and the unit test in
    // `tests/get-user/get-user.test.ts`.
    //
    // simpl_auth mode passes `skipCognitoBearer: true` (cookie-only) so
    // the probe answers "do I have a simpl_auth cookie *specifically*?".
    // cognito mode passes `false` so the Amplify Cognito ID token is sent
    // as a Bearer — the dual-issuer authorizer validates it against the
    // env's own Cognito pool and maps the caller by email.
    const response = await simplApiRequest<{
      caller?: CallerClaims | null;
    }>("GET", "/api/user", { skipCognitoBearer });
    const caller = response?.caller ?? null;
    if (!caller || !caller.email) {
      // 200 with an empty/missing caller means the Lambda ran without
      // an authorizer context — a server-side wiring bug, not an
      // unauthenticated user. Redirecting to sign in wouldn't fix it,
      // and the loop this used to produce is exactly what we're
      // guarding against. Surface it as the "service-unavailable"
      // state so the guard renders retry + sign-out affordances.
      return {
        status: "service-unavailable",
        caller: null,
        roles: [],
        probeStatus: 200
      };
    }
    return {
      status: "authed",
      caller,
      roles: rolesFromAuthUser({ caller })
    };
  } catch (err) {
    if (err instanceof SimplApiHttpError) {
      if (err.status === 401) {
        return { status: "unauthed", caller: null, roles: [] };
      }
      if (err.status === 403) {
        return {
          status: "forbidden",
          caller: null,
          roles: [],
          probeStatus: 403
        };
      }
      if (err.status === 404) {
        // SE-787: get-user's 404 branch still emits `caller` so the
        // impersonation trailer survives an unprovisioned target — a
        // SysAdmin using QA impersonation against an env that doesn't
        // have the target's user row hits this path and needs to see
        // (and clear) the impersonation banner from the error surface.
        const body = err.response as { caller?: CallerClaims | null } | undefined;
        const caller = body?.caller ?? null;
        return {
          status: "account-not-provisioned",
          caller,
          roles: caller ? rolesFromAuthUser({ caller }) : [],
          probeStatus: 404
        };
      }
      // Any other HTTP error — 5xx, unexpected 4xx like 429/408, etc.
      // Signed-in-but-server-broken. Not a sign-in problem.
      return {
        status: "service-unavailable",
        caller: null,
        roles: [],
        probeStatus: err.status
      };
    }
    // Non-`SimplApiHttpError` — network failure, timeout, aborted
    // fetch, JSON parse error before the client could wrap it, etc.
    // `probeStatus: null` signals "never reached the server" to the
    // ServiceUnavailable surface's developer-diagnostic paragraph.
    return {
      status: "service-unavailable",
      caller: null,
      roles: [],
      probeStatus: null
    };
  }
}

/**
 * Hook wrapper around `probeSimplAuthSession` plus the §6.7 Cognito
 * bootstrap bridge. Fires the probe on mount and exposes
 * `{ status, caller, roles, retry? }` to the consumer. State machine:
 *
 *   loading → probe `/api/user`
 *     ├── 200 w/ caller → authed
 *     ├── 200 w/o caller → service-unavailable (with retry)
 *     ├── 401 → bridging
 *     │     ├── attemptCognitoExchange() === "success" → re-probe
 *     │     │     └── (any terminal probe result surfaces as-is)
 *     │     ├── "mfa_enrollment_required" → stay in `bridging`
 *     │     │     (the bridge has already redirected window.location
 *     │     │     to /mfa/setup; further state changes would only
 *     │     │     flash UI before the navigation completes)
 *     │     └── "no_session" | "exchange_failed" → unauthed
 *     ├── 403 → forbidden
 *     ├── 404 → account-not-provisioned
 *     └── 5xx / network / timeout → service-unavailable (with retry)
 *
 * `bridging` keeps `<SimplAuthGuard>` on the "Updating your sign-in…"
 * splash so users with a leftover Cognito session don't briefly see
 * the sign-in redirect splash. On `unauthed` the guard renders its
 * `unauthedFallback`, which defaults to `<SignInRedirect>` and bounces
 * the browser to `simplAuthSignInUrl()`. Every other terminal state
 * renders an error surface with a Sign out button — deliberately NOT
 * a redirect, since redirecting cannot fix a 4xx-not-401 or a 5xx and
 * doing so produced the pre-SE-1765 tight loop.
 *
 * ``retry`` is populated only for ``service-unavailable`` (the sole
 * status where a transient fault might resolve without user action).
 * ``account-not-provisioned`` / ``forbidden`` do NOT expose retry — a
 * fresh probe against the same identity will always produce the same
 * failure, and offering a retry there would just recreate the loop we
 * fixed at a slower cadence.
 */
export function useSimplAuthSession(): SimplAuthSession {
  const [session, setSession] = useState<SimplAuthSession>({
    status: "loading",
    caller: null,
    roles: []
  });
  // Guards the effect and any retry() against a probe that resolves
  // after unmount. A single ref works for both because retry() runs
  // through the same setState path.
  const cancelledRef = useRef(false);

  const runProbe = useCallback(async () => {
    setSession({ status: "loading", caller: null, roles: [] });
    const initial = await probeSimplAuthSession();
    if (cancelledRef.current) return;

    if (initial.status !== "unauthed") {
      // authed, account-not-provisioned, forbidden, service-unavailable
      // all surface directly — the Cognito bridge only makes sense when
      // the whoami probe says "no simpl_auth cookie". None of the other
      // failures can be fixed by translating an Amplify session into a
      // simpl_auth one.
      setSession(
        initial.status === "service-unavailable"
          ? { ...initial, retry: runProbe }
          : initial
      );
      return;
    }

    setSession({ status: "bridging", caller: null, roles: [] });
    const bridge = await attemptCognitoExchange();
    if (cancelledRef.current) return;

    if (bridge === "success") {
      const reprobe = await probeSimplAuthSession();
      if (cancelledRef.current) return;
      setSession(
        reprobe.status === "service-unavailable"
          ? { ...reprobe, retry: runProbe }
          : reprobe
      );
      return;
    }

    if (bridge === "mfa_enrollment_required") {
      // Bridge already issued window.location.assign(setup_url).
      // Stay in `bridging` so the splash keeps rendering until the
      // browser actually navigates away.
      return;
    }

    // "no_session" or "exchange_failed" — fall through to the guard's
    // unauthed fallback for normal sign-in.
    setSession({ status: "unauthed", caller: null, roles: [] });
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    void runProbe();
    return () => {
      cancelledRef.current = true;
    };
  }, [runProbe]);

  useReloadOnIdentityChange(
    session.status === "authed" ? session.caller : null
  );

  return session;
}

const IDENTITY_RECHECK_MIN_INTERVAL_MS = 10_000;

/** Stable key for "who is this session acting as" — target + impersonator. */
export const sessionIdentityKey = (caller: CallerClaims): string =>
  `${caller.email.toLowerCase()}|${(caller.impersonatedBy ?? "").toLowerCase()}`;

/**
 * The session cookie is shared across SIMPL domains, so starting or ending
 * an impersonation on reports-* (or signing in as someone else in another
 * tab) swaps the identity under this SPA without reloading it — and Redux
 * keeps the previous user's roles and data. When the tab regains focus,
 * re-probe; if the identity changed (or the session ended), reload so
 * every piece of state is rebuilt for the identity the server now sees.
 */
function useReloadOnIdentityChange(caller: CallerClaims | null): void {
  const identity = caller ? sessionIdentityKey(caller) : null;
  useEffect(() => {
    if (!identity || typeof window === "undefined") return;
    let lastCheck = Date.now();
    let inFlight = false;
    const recheck = async () => {
      if (document.visibilityState === "hidden") return;
      if (inFlight || Date.now() - lastCheck < IDENTITY_RECHECK_MIN_INTERVAL_MS) {
        return;
      }
      inFlight = true;
      lastCheck = Date.now();
      try {
        const probe = await probeSimplAuthSession();
        const changed =
          probe.status === "unauthed" ||
          (probe.status === "authed" &&
            !!probe.caller &&
            sessionIdentityKey(probe.caller) !== identity);
        if (changed) window.location.reload();
      } finally {
        inFlight = false;
      }
    };
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", recheck);
    return () => {
      window.removeEventListener("focus", recheck);
      document.removeEventListener("visibilitychange", recheck);
    };
  }, [identity]);
}
